
import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import CustomImage from '../TipTapEditor/TipTapImageExtension';
import '../TipTapEditor/TipTapEditor.css';
import './TipTapOpcionEditor.css';

const TipTapOpcionEditor = ({ 
  value = '', 
  handleChange, 
  placeholder = 'Escriba la opción de respuesta...',
  allowImages = false
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const fileInputRef = useRef(null);

  const extensions = [
    StarterKit.configure({
      heading: false,
      blockquote: false,
      codeBlock: false,
      horizontalRule: false,
      history: {
        depth: 50,
      },
    }),
    Underline,
    Placeholder.configure({
      placeholder: placeholder,
      emptyEditorClass: 'is-editor-empty',
    }),
  ];

  if (allowImages) {
    extensions.push(CustomImage);
  }

  const editor = useEditor({
    extensions,
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (handleChange) {
        handleChange(html);
      }
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor-content tiptap-opcion-content',
        spellcheck: 'false',
      },
      handleDrop: (view, event, slice, moved) => {
        if (allowImages && !moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
          const file = event.dataTransfer.files[0];
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            handleImageUpload(file);
            return true;
          }
        }
        return false;
      },
      handlePaste: (view, event) => {
        if (allowImages) {
          const items = event.clipboardData?.items;
          if (items) {
            for (let i = 0; i < items.length; i++) {
              if (items[i].type.startsWith('image/')) {
                const file = items[i].getAsFile();
                if (file) {
                  event.preventDefault();
                  handleImageUpload(file);
                  return true;
                }
              }
            }
          }
        }
        return false;
      },
    },
    autofocus: false,
  }, []);

  useEffect(() => {
    if (editor && value !== editor.getHTML() && !isInitialized) {
      editor.commands.setContent(value || '');
      setIsInitialized(true);
    }
  }, [editor, value, isInitialized]);

  const handleImageUpload = useCallback((file) => {
    if (!editor || !allowImages) return;

    setIsUploading(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      const src = e.target.result;
      
      const img = new Image();
      img.onload = () => {
        const maxWidth = 200;
        const ratio = img.width / img.height;
        const width = Math.min(img.width, maxWidth);
        const height = width / ratio;

        editor.chain().focus().setImage({
          src,
          alt: file.name,
          width: Math.round(width),
          height: Math.round(height),
          alignment: 'left',
        }).run();

        setIsUploading(false);
      };
      img.src = src;
    };

    reader.onerror = () => {
      console.error('Error al leer el archivo de imagen');
      setIsUploading(false);
    };

    reader.readAsDataURL(file);
  }, [editor, allowImages]);

  const handleImageButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleImageUpload(file);
    }
  };

  const ToolbarButton = ({ onClick, isActive, disabled, children, title }) => (
    <button
      onMouseDown={(e) => { e.preventDefault(); if (!disabled) onClick(e); }}
      disabled={disabled}
      className={`tiptap-toolbar-btn tiptap-toolbar-btn-mini ${isActive ? 'is-active' : ''}`}
      title={title}
      type="button"
    >
      {children}
    </button>
  );

  if (!editor) {
    return <div className="tiptap-loading-mini">Cargando...</div>;
  }

  return (
    <div className="tiptap-opcion-editor-wrapper">
      <div className="tiptap-opcion-toolbar">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Negrita"
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Cursiva"
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Subrayado"
        >
          <u>U</u>
        </ToolbarButton>
        {allowImages && (
          <>
            <div className="tiptap-toolbar-divider-mini" />
            <ToolbarButton
              onClick={handleImageButtonClick}
              disabled={isUploading}
              title="Insertar imagen"
            >
              {isUploading ? '⏳' : '🖼️'}
            </ToolbarButton>
          </>
        )}
      </div>

      <div className="tiptap-opcion-content-container">
        <EditorContent editor={editor} />
      </div>

      {allowImages && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
      )}
    </div>
  );
};

export default TipTapOpcionEditor;
